import { NodeChange, OnNodesChange, useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import {
  FamilyTreeNode,
  FlowChartProps,
  Node,
} from '@/components/diagrams/FlowChart';
import { useHelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';

import { useFamilyTreeContext } from '../contexts';

export function useFlowChartProps() {
  const { state, dispatch } = useFamilyTreeContext();
  const { isInteractive } = useIsInteractive();
  const { getNodes } = useReactFlow();
  const { helperLineVertical, helperLineHorizontal, getHelperLinesChanges } =
    useHelperLines({
      nodeTypes: isInteractive ? ['marriageGroup', 'person'] : [],
      horizontal: {
        top: true,
        center: false,
        bottom: false,
      },
      vertical: {
        left: true,
        right: true,
        center: true,
      },
    });

  const onNodesChange = useCallback<OnNodesChange<FamilyTreeNode>>(
    (changes) => {
      // Apply helper lines to the changes before dispatching
      const modifiedChanges = getHelperLinesChanges(
        changes,
        getNodes() as Node[]
      );

      dispatch({
        type: 'NODES_CHANGED',
        changes: modifiedChanges as NodeChange<FamilyTreeNode>[],
      });
    },
    [dispatch, getHelperLinesChanges, getNodes]
  );

  const onNodeClick = useCallback<
    NonNullable<FlowChartProps<FamilyTreeNode>['onNodeClick']>
  >(
    (event, _node) => {
      // Don't open side panel when in edit mode
      if (isInteractive) return;

      const el = event.target as HTMLElement | null;
      const personIdElement = el?.closest?.(
        '[data-person-id]'
      ) as HTMLElement | null;

      if (!personIdElement) return;

      const personId = personIdElement.getAttribute('data-person-id')!;

      // Ensure personId is a string
      if (typeof personId !== 'string') return;

      // If clicking on the already selected person, close the panel
      if (personId === state.selectedPersonId) {
        dispatch({ type: 'CLOSE_SIDE_PANEL' });
        return;
      }

      // Open the side panel for this person
      dispatch({ type: 'OPEN_SIDE_PANEL', personId });
    },
    [dispatch, isInteractive, state.selectedPersonId]
  );

  const onPaneClick = useCallback<
    NonNullable<FlowChartProps<FamilyTreeNode>['onPaneClick']>
  >(() => {
    // Close the side panel when clicking on empty space
    if (state.isSidePanelOpen) {
      dispatch({ type: 'CLOSE_SIDE_PANEL' });
    }
  }, [dispatch, state.isSidePanelOpen]);

  const flowChartProps: FlowChartProps<FamilyTreeNode> = {
    nodes: state.nodes,
    edges: state.edges,
    onNodesChange,
    onNodeClick,
    onPaneClick,
  };

  return { flowChartProps, helperLineHorizontal, helperLineVertical };
}
